import { useEffect, useRef } from "react";

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function focusableWithin(panel: HTMLElement): HTMLElement[] {
  return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (node) => node.offsetParent !== null || node === document.activeElement
  );
}

export interface DialogBehaviourOptions {
  open: boolean;
  onDismiss: () => void;
  /** False while an action is in flight, so Escape can't abandon it. */
  dismissible?: boolean;
}

// Gives a dialog the three things a modal owes the keyboard: focus moves in
// when it opens, Tab cannot leave it, and focus returns to whatever opened
// it on close. Escape dismisses unless an action is mid-flight.
export function useDialogBehaviour({
  open,
  onDismiss,
  dismissible = true,
}: DialogBehaviourOptions) {
  const panel = useRef<HTMLDivElement | null>(null);
  // Held in a ref so an inline arrow prop doesn't re-run the effect on every
  // render, which would steal focus back to the first control mid-interaction.
  // Synced in its own effect (declared first, so it lands before the effect
  // below reads it) rather than during render, which would touch a ref while
  // rendering.
  const dismiss = useRef(onDismiss);
  useEffect(() => {
    dismiss.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const node = panel.current;
    if (node) {
      const preferred = node.querySelector<HTMLElement>("[data-autofocus]");
      (preferred ?? focusableWithin(node)[0] ?? node).focus();
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        if (!dismissible) return;
        event.stopPropagation();
        dismiss.current();
        return;
      }
      if (event.key !== "Tab" || !panel.current) return;
      const items = focusableWithin(panel.current);
      if (!items.length) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      const outside = !panel.current.contains(active);
      if (event.shiftKey && (active === first || outside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || outside)) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      // The opener is often a menu item that has already unmounted with its
      // menu by the time this effect runs, leaving <body> focused — which is
      // "connected" but not a real restore target. Fall back to the control
      // that owns the menu so focus never lands on the document.
      const restorable =
        previouslyFocused && previouslyFocused !== document.body && previouslyFocused.isConnected;
      if (restorable) previouslyFocused.focus();
      else document.querySelector<HTMLElement>('[aria-haspopup="menu"]')?.focus();
    };
  }, [open, dismissible]);

  return panel;
}
